<template>
  <div class="social-message-composer">
    <div v-if="replyTo" class="social-message-composer__reply-tag">
      <div class="social-message-composer__reply-tag-text">
        <div class="social-message-composer__reply-tag-title">
          Reply to {{ replyToDisplayName }}
        </div>
        <div class="social-message-composer__reply-tag-snippet">{{ replyToSnippet }}</div>
      </div>
      <button
        type="button"
        class="social-message-composer__reply-tag-close"
        title="Cancel reply"
        @click="emit('cancel-reply')"
      >
        <Icon icon="mdi:close" />
      </button>
    </div>

    <div
      v-if="attachments.length > 0 || voiceMessage || videoMessage || isVideoRecording"
      class="social-message-composer__previews"
    >
      <div
        v-for="(file, index) in attachments"
        :key="index"
        class="social-message-composer__preview-item"
      >
        <Icon :icon="getFileIcon(file.type)" class="social-message-composer__preview-icon" />
        <div class="social-message-composer__preview-info">
          <div class="social-message-composer__preview-name">{{ file.name }}</div>
          <div class="social-message-composer__preview-size">{{ formatFileSize(file.size) }}</div>
        </div>
        <button
          type="button"
          class="social-message-composer__preview-remove"
          @click="removeAttachment(index)"
          title="Remove file"
        >
          <Icon icon="mdi:close" />
        </button>
      </div>
      <div v-if="voiceMessage" class="social-message-composer__preview-item">
        <Icon icon="mdi:microphone" class="social-message-composer__preview-icon" />
        <div class="social-message-composer__preview-info">
          <div class="social-message-composer__preview-name">{{ voiceMessage.name }}</div>
          <div class="social-message-composer__preview-size">
            {{ formatFileSize(voiceMessage.size) }}
          </div>
        </div>
        <audio
          v-if="voiceMessageUrl"
          :src="voiceMessageUrl"
          controls
          class="social-message-composer__preview-audio"
        />
        <button
          type="button"
          class="social-message-composer__preview-remove"
          @click="resetVoice"
          title="Remove voice message"
        >
          <Icon icon="mdi:close" />
        </button>
      </div>
      <div v-if="videoMessage" class="social-message-composer__preview-item">
        <Icon icon="mdi:video-outline" class="social-message-composer__preview-icon" />
        <div class="social-message-composer__preview-info">
          <div class="social-message-composer__preview-name">{{ videoMessage.name }}</div>
          <div class="social-message-composer__preview-size">
            {{ formatFileSize(videoMessage.size) }}
          </div>
        </div>
        <video
          v-if="videoMessageUrl"
          :src="videoMessageUrl"
          controls
          class="social-message-composer__preview-video"
        />
        <button
          type="button"
          class="social-message-composer__preview-remove"
          @click="resetVideo"
          title="Remove video message"
        >
          <Icon icon="mdi:close" />
        </button>
      </div>
      <div v-if="isVideoRecording" class="social-message-composer__video-live-preview">
        <video
          ref="liveVideoRef"
          autoplay
          muted
          playsinline
          class="social-message-composer__video-live"
        />
        <div class="social-message-composer__video-live-badge">
          <span class="social-message-composer__video-live-dot" />
          {{ videoRecordingTime }}
        </div>
      </div>
    </div>

    <IcpPicker
      :show="showIcpPicker"
      :mode="icpPickerMode"
      :icps="icps"
      class="social-message-composer__icp-picker-positioned"
      @select="selectIcp"
      @dismiss="dismissIcpPicker"
    />

    <div class="social-message-composer__resizable-area" :style="{ height: editorHeight + 'px' }">
      <div
        class="social-message-composer__resize-handle"
        :class="{ 'is-resizing': isResizingEditor }"
        title="Drag to resize"
        @mousedown="startEditorResize"
      ></div>

      <div v-if="showAiButtons" class="social-message-composer__ai-buttons-wrapper">
        <button
          ref="aiComposeButtonRef"
          type="button"
          class="social-message-composer__ai-btn social-message-composer__ai-btn--compose"
          :disabled="aiButtonsDisabled || isGeneratingAI"
          :title="isGeneratingAI ? 'Generating...' : 'AI Compose'"
          @click.stop="handleAiCompose"
        >
          <template v-if="isGeneratingAI && generatingAction === 'compose'">
            <span class="social-message-composer__ai-spinner" />
            <span class="social-message-composer__ai-btn-text">Generating...</span>
          </template>
          <template v-else>
            <Icon icon="mdi:sparkles" />
            <span class="social-message-composer__ai-btn-text">AI Compose</span>
          </template>
        </button>
        <button
          type="button"
          class="social-message-composer__ai-btn social-message-composer__ai-btn--reply"
          :disabled="aiButtonsDisabled || isGeneratingAI"
          :title="isGeneratingAI ? 'Generating...' : 'AI Reply'"
          @click.stop="handleAiReply"
        >
          <template v-if="isGeneratingAI && generatingAction === 'reply'">
            <span class="social-message-composer__ai-spinner" />
            <span class="social-message-composer__ai-btn-text">Generating...</span>
          </template>
          <template v-else>
            <Icon icon="mdi:sparkles" />
            <span class="social-message-composer__ai-btn-text">AI Reply</span>
          </template>
        </button>
        <button
          type="button"
          class="social-message-composer__ai-btn social-message-composer__ai-btn--followup"
          :disabled="aiButtonsDisabled || isGeneratingAI"
          :title="isGeneratingAI ? 'Generating...' : 'AI Follow-up'"
          @click.stop="handleAiFollowup"
        >
          <template v-if="isGeneratingAI && generatingAction === 'followup'">
            <span class="social-message-composer__ai-spinner" />
            <span class="social-message-composer__ai-btn-text">Generating...</span>
          </template>
          <template v-else>
            <Icon icon="mdi:sparkles" />
            <span class="social-message-composer__ai-btn-text">AI Follow-up</span>
          </template>
        </button>
        <button
          ref="aiRefineButtonRef"
          type="button"
          class="social-message-composer__ai-btn social-message-composer__ai-btn--custom"
          :disabled="aiButtonsDisabled || isGeneratingAI || isGeneratingAiCustom"
          :title="isGeneratingAiCustom ? 'Generating...' : 'AI Refine'"
          @click.stop="toggleAiRefinePanel"
        >
          <Icon icon="mdi:sparkles" />
          <span class="social-message-composer__ai-btn-text">AI Refine</span>
        </button>
      </div>

      <div class="social-message-composer__editor-wrapper">
        <RichTextEditor
          ref="richTextEditor"
          v-model="messageContent"
          :height="'100%'"
          :toolbar="[]"
          placeholder="Type your message..."
          @enter-pressed="handleSend"
        />
      </div>
    </div>

    <div
      v-if="showEmojiPopover"
      class="social-message-composer__emoji-container"
      @click.self="closeEmojiPopover"
    >
      <EmojiPicker @select="handleEmojiSelect" />
    </div>
    <AiRefinePopover
      v-if="showAiButtons"
      :show="showAiPanel"
      :trigger-el="aiRefineButtonRef"
      :disabled="isGeneratingAiCustom"
      @generate="handleAiCustomizationGenerate"
      @close="showAiPanel = false"
    />

    <AiRefinePopover
      v-if="showAiButtons"
      :show="showAiComposePanel"
      :trigger-el="aiComposeButtonRef"
      :disabled="isGeneratingAI && generatingAction === 'compose'"
      title="AI Compose"
      generate-button-label="Generate Message"
      :show-attachments="false"
      @generate="handleAiComposeGenerate"
      @close="showAiComposePanel = false"
    />

    <div class="social-message-composer__footer">
      <div class="social-message-composer__footer-left">
        <button
          type="button"
          class="social-message-composer__footer-icon-btn"
          title="Attach files"
          @click="handleAttachment('file')"
        >
          <Icon icon="mdi:paperclip" />
        </button>
        <button
          v-if="supportsVoiceMessage"
          type="button"
          class="social-message-composer__footer-icon-btn"
          :class="{ 'is-recording': isRecording }"
          :disabled="isVideoRecording"
          title="Record voice message"
          @click="handleAttachment('voice')"
        >
          <Icon :icon="isRecording ? 'mdi:stop-circle-outline' : 'mdi:microphone-outline'" />
        </button>
        <span v-if="isRecording" class="social-message-composer__recording-time">
          {{ recordingTime }}
        </span>
        <button
          v-if="supportsVideoMessage"
          type="button"
          class="social-message-composer__footer-icon-btn"
          :class="{ 'is-recording': isVideoRecording }"
          :disabled="isRecording"
          title="Record video message"
          @click="isVideoRecording ? stopVideoRecording() : startVideoRecording()"
        >
          <Icon :icon="isVideoRecording ? 'mdi:stop-circle-outline' : 'mdi:video-outline'" />
        </button>
        <span v-if="isVideoRecording" class="social-message-composer__recording-time">
          {{ videoRecordingTime }}
        </span>
      </div>
      <div class="social-message-composer__footer-right" ref="sendButtonRef">
        <div class="social-message-composer__footer-inline-tools">
          <div class="social-message-composer__popover-wrapper" ref="linkPopoverWrapperRef">
            <button
              type="button"
              class="social-message-composer__footer-icon-btn"
              title="Insert link"
              @click.stop="toggleLinkPopover"
            >
              <Icon icon="mdi:link" />
            </button>
            <div
              v-if="showLinkPopover"
              ref="linkPopoverRef"
              class="social-message-composer__popover social-message-composer__popover--link"
              :style="popoverStyle"
              @click.stop
            >
              <div class="social-message-composer__popover-content">
                <div class="social-message-composer__link-field-wrapper">
                  <Icon
                    icon="mdi:format-align-left"
                    class="social-message-composer__link-field-icon"
                  />
                  <input
                    v-model="linkText"
                    type="text"
                    class="social-message-composer__link-input social-message-composer__link-input--text"
                    placeholder="Text"
                    @keyup.enter="insertLink"
                    @keyup.esc="closeLinkPopover"
                  />
                </div>
                <div
                  class="social-message-composer__link-field-wrapper social-message-composer__link-field-wrapper--url"
                >
                  <Icon icon="mdi:link" class="social-message-composer__link-field-icon" />
                  <input
                    v-model="linkUrl"
                    type="text"
                    class="social-message-composer__link-input social-message-composer__link-input--url"
                    placeholder="Type or paste a link"
                    @keyup.enter="insertLink"
                    @keyup.esc="closeLinkPopover"
                    ref="linkInputRef"
                  />
                  <button
                    type="button"
                    class="social-message-composer__link-apply-btn"
                    @click="insertLink"
                    :disabled="!linkUrl || linkUrl.trim() === ''"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          </div>
          <button
            type="button"
            class="social-message-composer__footer-icon-btn"
            title="Insert emoji"
            @click.stop="toggleEmojiPopover"
          >
            <Icon icon="mdi:emoticon-outline" />
          </button>
        </div>
        <div class="social-message-composer__split-button">
          <button
            class="social-message-composer__send-btn"
            :class="{ 'social-message-composer__send-btn--disabled': !canSend }"
            @click="handleSend"
            :disabled="!canSend"
          >
            <Icon icon="mdi:send" />
            Send
          </button>
          <button
            type="button"
            class="social-message-composer__send-dropdown-btn"
            :disabled="!canSend"
            @click.stop="toggleScheduleMenu"
            title="Schedule send"
          >
            <Icon icon="mdi:chevron-down" />
          </button>
        </div>
        <div
          v-if="showScheduleMenu && canSend"
          class="social-message-composer__schedule-menu"
          @click.stop
        >
          <button
            type="button"
            class="social-message-composer__schedule-menu-item"
            @click="handleScheduleMenuClick"
          >
            <Icon icon="mdi:clock-outline" />
            Schedule send
          </button>
        </div>
      </div>
    </div>

    <ScheduleSendModal
      :show="showScheduleModal"
      :is-loading="isScheduling"
      @close="showScheduleModal = false"
      @schedule="handleScheduleSend"
    />

    <ActionConfirmationModal
      :show="showAiActionConfirm"
      :title="aiActionConfirmTitle"
      :message="aiActionConfirmMessage"
      confirm-text="Generate"
      cancel-text="Cancel"
      confirm-variant="primary"
      dialog-max-width="340px"
      @confirm="confirmAiAction"
      @close="cancelAiAction"
    />
  </div>
</template>

<script setup lang="ts">
import ActionConfirmationModal from "@/components/modal/ActionConfirmationModal.vue";
import ScheduleSendModal from "@/components/socials/ScheduleSendModal.vue";
import RichTextEditor from "@/components/ui/RichTextEditor.vue";
import type { Conversation, Message } from "@/dtos/Unipile.dto";
import { getEditorHTML, getQuillInstance, isBodyEmpty } from "@/utils/emailComposerHelpers";
import { Icon } from "@iconify/vue";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useToast } from "@/composables/ui/useToast";
import EmojiPicker from "./EmojiPicker.vue";
import AiRefinePopover from "./AiRefinePopover.vue";
import IcpPicker from "@/components/ui/IcpPicker.vue";
import AIGenerationService from "@/services/ai/ai-generation";
import { useIcpPicker } from "@/composables/ui/useIcpPicker";
import { useMessageDraft } from "@/composables/messages/useMessageDraft";
import type { MessageDraftChannel } from "@/dtos/MessageDraft.dto";

interface Props {
  conversation: Conversation | null;
  replyTo?: Message | null;
  channelStatus?: string;
  isSending?: boolean;
  showAiButtons?: boolean;
  aiButtonsDisabled?: boolean;
  isGeneratingAI?: boolean;
  generatingAction?: string | null;
  contactId?: string;
  icpId?: string;
  icps?: Array<{ id: string; name: string; isDefault?: boolean }>;
  channelName?: string;
}

const props = withDefaults(defineProps<Props>(), {
  isSending: false,
  showAiButtons: false,
  aiButtonsDisabled: false,
  isGeneratingAI: false,
});

const $toast = useToast();

const emit = defineEmits<{
  "send-message": [
    data: {
      text?: string;
      quote_id?: string;
      attachments?: File[];
      voice_message?: File;
      video_message?: File;
    },
  ];
  "schedule-send": [
    data: {
      text: string;
      quote_id?: string;
      attachments?: File[];
      voice_message?: File;
      video_message?: File;
      scheduledAt: string;
    },
  ];
  "cancel-reply": [];
  "ai-reply": [payload: { icpId?: string }];
  "ai-followup": [payload: { icpId?: string }];
  "ai-compose": [payload: { icpId?: string; customInstructions?: string }];
}>();

const richTextEditor = ref<InstanceType<typeof RichTextEditor> | null>(null);
const messageContent = ref("");

const draftChannel = (props.channelName ||
  props.conversation?.channel ||
  "linkedin") as MessageDraftChannel;
const { fetchDraft, scheduleSave, flushSave, clearDraft } = useMessageDraft(draftChannel);
let loadedDraftConversationId = "";
const showEmojiPopover = ref(false);
const showLinkPopover = ref(false);
const linkText = ref("");
const linkUrl = ref("");
const linkInputRef = ref<HTMLInputElement | null>(null);
const linkPopoverRef = ref<HTMLElement | null>(null);
const linkPopoverWrapperRef = ref<HTMLElement | null>(null);
const sendButtonRef = ref<HTMLElement | null>(null);
const popoverStyle = ref<{ bottom?: string; right?: string; left?: string }>({});

const attachments = ref<File[]>([]);

// Voice recording
const isRecording = ref(false);
const recordingTime = ref("00:00");
const voiceMessage = ref<File | null>(null);
let mediaRecorder: MediaRecorder | null = null;
let audioStream: MediaStream | null = null;
let audioChunks: Blob[] = [];
let recordingStart = 0;
let recordingTimer: number | null = null;

// Video recording
const isVideoRecording = ref(false);
const videoRecordingTime = ref("00:00");
const videoMessage = ref<File | null>(null);
const liveVideoRef = ref<HTMLVideoElement | null>(null);
let videoMediaRecorder: MediaRecorder | null = null;
let videoStream: MediaStream | null = null;
let videoChunks: Blob[] = [];
let videoRecordingStart = 0;
let videoRecordingTimer: number | null = null;
let videoAutoStopTimeout: number | null = null;

const showScheduleModal = ref(false);
const showScheduleMenu = ref(false);
const isScheduling = ref(false);
const showAiPanel = ref(false);
const showAiComposePanel = ref(false);
const isGeneratingAiCustom = ref(false);
const aiRefineButtonRef = ref<HTMLElement | null>(null);
const aiComposeButtonRef = ref<HTMLElement | null>(null);
const pendingComposeIcpId = ref<string | undefined>(undefined);

// Editor resize (drag the handle above the AI buttons to grow/shrink the message box)
const MIN_EDITOR_HEIGHT = 90;
const AI_BUTTONS_ROW_HEIGHT = 34;
const MAX_EDITOR_HEIGHT = 360;
const minResizableHeight = computed(
  () => MIN_EDITOR_HEIGHT + (props.showAiButtons ? AI_BUTTONS_ROW_HEIGHT : 0)
);
const editorHeight = ref(MIN_EDITOR_HEIGHT + AI_BUTTONS_ROW_HEIGHT);
const isResizingEditor = ref(false);
let resizeStartY = 0;
let resizeStartHeight = 0;

const handleEditorResizeMove = (event: MouseEvent) => {
  if (!isResizingEditor.value) return;
  const delta = resizeStartY - event.clientY;
  editorHeight.value = Math.min(
    MAX_EDITOR_HEIGHT,
    Math.max(minResizableHeight.value, resizeStartHeight + delta)
  );
};

const stopEditorResize = () => {
  if (!isResizingEditor.value) return;
  isResizingEditor.value = false;
  window.removeEventListener("mousemove", handleEditorResizeMove);
  window.removeEventListener("mouseup", stopEditorResize);
  document.body.style.cursor = "";
  document.body.style.userSelect = "";
};

const startEditorResize = (event: MouseEvent) => {
  event.preventDefault();
  isResizingEditor.value = true;
  resizeStartY = event.clientY;
  resizeStartHeight = editorHeight.value;
  window.addEventListener("mousemove", handleEditorResizeMove);
  window.addEventListener("mouseup", stopEditorResize);
  document.body.style.cursor = "ns-resize";
  document.body.style.userSelect = "none";
};

const supportsVoiceMessage = computed(() => {
  const channel = props.conversation?.channel;
  return channel === "whatsapp" || channel === "linkedin";
});

const supportsVideoMessage = computed(() => {
  const channel = props.conversation?.channel;
  return channel === "whatsapp" || channel === "linkedin";
});

const replyTo = computed(() => props.replyTo || null);

const replyToDisplayName = computed(() => {
  if (!props.replyTo) return "";
  if (props.replyTo.isOwn) return "You";
  return props.replyTo.senderName || "Unknown";
});

const stripHtml = (html: string) => html.replace(/<[^>]*>/g, "");

const replyToSnippet = computed(() => {
  if (!props.replyTo) return "";
  const raw = (props.replyTo.text || "").toString();
  const cleaned = stripHtml(raw).replace(/\s+/g, " ").trim();
  if (!cleaned) return " ";
  return cleaned.length > 90 ? cleaned.slice(0, 90) + "…" : cleaned;
});

const isStatusGood = computed(() => {
  if (!props.channelStatus) return false;
  const status = props.channelStatus.toUpperCase();
  return ["OK", "CREATION_SUCCESS", "RECONNECTED", "SYNC_SUCCESS"].includes(status);
});

const canSend = computed(() => {
  const htmlContent = getEditorHTML(richTextEditor.value as any, messageContent.value);
  const hasValidContent = htmlContent && !isBodyEmpty(htmlContent);
  const hasAttachments = attachments.value.length > 0;
  const hasVoice = !!voiceMessage.value;
  const hasVideo = !!videoMessage.value;

  if (!hasAttachments && !hasVoice && !hasVideo) {
    return hasValidContent && isStatusGood.value && !isRecording.value && !isVideoRecording.value;
  }

  return (
    (hasValidContent || hasAttachments || hasVoice || hasVideo) &&
    isStatusGood.value &&
    !isRecording.value &&
    !isVideoRecording.value
  );
});

const getQuill = () => getQuillInstance(richTextEditor.value as any);

const toggleLinkPopover = () => {
  if (showLinkPopover.value) {
    closeLinkPopover();
  } else {
    closeAllPopovers();

    const quill = getQuill();
    if (quill) {
      const range = quill.getSelection(true);
      if (range && range.length > 0) {
        const selectedText = quill.getText(range.index, range.length);
        linkText.value = selectedText.trim();
      }
    }

    showLinkPopover.value = true;
    nextTick(() => {
      if (linkPopoverWrapperRef.value) {
        const wrapperRect = linkPopoverWrapperRef.value.getBoundingClientRect();
        const popoverWidth = 240;
        const spacing = 8;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        let right: string | undefined = undefined;
        let left: string | undefined = undefined;
        const rightPosition = viewportWidth - wrapperRect.right;

        if (wrapperRect.right - popoverWidth < 16) {
          left = "16px";
          right = undefined;
        } else {
          right = `${rightPosition}px`;
          left = undefined;
        }

        const bottom = `${viewportHeight - wrapperRect.top + spacing}px`;

        popoverStyle.value = {
          bottom,
          right,
          left,
        };
      }

      if (linkInputRef.value) {
        linkInputRef.value.focus();
      }
    });
  }
};

const closeLinkPopover = () => {
  showLinkPopover.value = false;
  linkUrl.value = "";
  linkText.value = "";
};

const insertLink = () => {
  const quill = getQuill();
  if (!quill) return;

  if (!linkUrl.value || linkUrl.value.trim() === "") {
    return;
  }

  try {
    const range = quill.getSelection(true);
    if (!range) {
      closeLinkPopover();
      return;
    }

    let url = linkUrl.value.trim();
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      url = "https://" + url;
    }

    const text = linkText.value.trim() || url;

    if (range.length > 0) {
      quill.formatText(range.index, range.length, "link", url);
    } else {
      quill.insertText(range.index, text, "link", url);
      quill.setSelection(range.index + text.length);
    }

    closeLinkPopover();
  } catch {
    closeLinkPopover();
  }
};

const toggleEmojiPopover = () => {
  if (showEmojiPopover.value) {
    showEmojiPopover.value = false;
  } else {
    closeAllPopovers();
    showEmojiPopover.value = true;
  }
};

const closeEmojiPopover = () => {
  showEmojiPopover.value = false;
};

const handleEmojiSelect = (emoji: any) => {
  const quill = getQuill();
  if (!quill) {
    closeEmojiPopover();
    return;
  }

  try {
    const range = quill.getSelection(true);
    const index = range ? range.index : quill.getLength() - 1;

    const emojiChar = emoji.emoji || emoji.native || emoji;
    quill.insertText(index, emojiChar);
    quill.setSelection(index + emojiChar.length);

    closeEmojiPopover();
  } catch {
    closeEmojiPopover();
  }
};

const handleAttachment = (type: string) => {
  if (type === "voice") {
    if (!supportsVoiceMessage.value) return;
    if (isRecording.value) {
      stopRecording();
    } else {
      startRecording();
    }
    return;
  }

  const input = document.createElement("input");
  input.type = "file";
  input.multiple = true;

  if (type === "photo") {
    input.accept = "image/*";
  } else if (type === "video") {
    input.accept = "video/*";
  } else {
    input.accept = "*/*";
  }

  input.onchange = async (e: Event) => {
    const target = e.target as HTMLInputElement;
    const files = Array.from(target.files || []);
    if (!files.length) return;

    if (!validateFiles(files)) {
      target.value = "";
      return;
    }

    addAttachments(files);
    target.value = "";
  };

  input.click();
};

const validateFiles = (files: File[]) => {
  for (const file of files) {
    if (file.size > 16 * 1024 * 1024) {
      $toast?.error?.(`File "${file.name}" is too large. Maximum size is 16MB.`);
      return false;
    }
  }
  return true;
};

const addAttachments = (files: File[]) => {
  attachments.value.push(...files);
};

const removeAttachment = (index: number) => {
  attachments.value.splice(index, 1);
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
};

const getFileIcon = (mimeType: string): string => {
  if (mimeType.startsWith("image/")) return "mdi:image-outline";
  if (mimeType.startsWith("video/")) return "mdi:video-outline";
  if (mimeType.includes("pdf")) return "mdi:file-pdf-box";
  if (mimeType.includes("word") || mimeType.includes("document")) return "mdi:file-word";
  if (mimeType.includes("excel") || mimeType.includes("spreadsheet")) return "mdi:file-excel";
  return "mdi:file-outline";
};

const voiceMessageUrl = computed((): string | undefined => {
  if (!voiceMessage.value) return undefined;
  return URL.createObjectURL(voiceMessage.value);
});

const videoMessageUrl = computed((): string | undefined => {
  if (!videoMessage.value) return undefined;
  return URL.createObjectURL(videoMessage.value);
});

const closeAllPopovers = () => {
  showLinkPopover.value = false;
  showEmojiPopover.value = false;
};

const startRecording = async () => {
  try {
    audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    mediaRecorder = new MediaRecorder(audioStream, { mimeType: "audio/webm" });
    audioChunks.length = 0;

    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size) audioChunks.push(e.data);
    };

    mediaRecorder.onstop = () => {
      if (!audioChunks.length) return;
      const blob = new Blob(audioChunks, { type: "audio/webm" });
      const fileName = `voice_message_${Date.now()}.webm`;
      const file = new File([blob], fileName, { type: "audio/webm" });

      voiceMessage.value = file;
    };

    recordingStart = Date.now();
    isRecording.value = true;
    recordingTime.value = "00:00";
    recordingTimer = window.setInterval(() => {
      const elapsed = Date.now() - recordingStart;
      const sec = Math.floor(elapsed / 1000);
      const min = Math.floor(sec / 60);
      const s = sec % 60;
      recordingTime.value = `${String(min).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    }, 1000);

    mediaRecorder.start();
  } catch (err) {
    console.error("Error starting recording:", err);
    $toast?.error?.("Unable to access microphone.");
    isRecording.value = false;
  }
};

const stopRecording = () => {
  if (!isRecording.value) return;
  isRecording.value = false;
  if (recordingTimer !== null) {
    clearInterval(recordingTimer);
    recordingTimer = null;
  }
  if (mediaRecorder && mediaRecorder.state !== "inactive") {
    mediaRecorder.stop();
  }
  if (audioStream) {
    audioStream.getTracks().forEach((t) => t.stop());
    audioStream = null;
  }
};

const resetVoice = () => {
  if (isRecording.value) stopRecording();
  voiceMessage.value = null;
  recordingTime.value = "00:00";
};

const startVideoRecording = async () => {
  if (videoStream) {
    videoStream.getTracks().forEach((t) => t.stop());
    videoStream = null;
  }
  try {
    videoStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });

    const mimeTypes = ["video/mp4", "video/webm;codecs=h264", "video/webm"];
    const mimeType = mimeTypes.find((t) => MediaRecorder.isTypeSupported(t)) || "video/webm";
    const ext = mimeType.startsWith("video/mp4") ? "mp4" : "webm";

    videoMediaRecorder = new MediaRecorder(videoStream, { mimeType });
    videoChunks = [];

    videoMediaRecorder.ondataavailable = (e) => {
      if (e.data.size) videoChunks.push(e.data);
    };

    videoMediaRecorder.onstop = () => {
      if (!videoChunks.length) return;
      const blob = new Blob(videoChunks, { type: mimeType });
      const fileName = `video_message_${Date.now()}.${ext}`;
      videoMessage.value = new File([blob], fileName, { type: mimeType });
    };

    videoRecordingStart = Date.now();
    isVideoRecording.value = true;
    videoRecordingTime.value = "00:00";
    nextTick(() => {
      if (liveVideoRef.value) liveVideoRef.value.srcObject = videoStream;
    });
    videoRecordingTimer = window.setInterval(() => {
      const elapsed = Date.now() - videoRecordingStart;
      const sec = Math.floor(elapsed / 1000);
      const min = Math.floor(sec / 60);
      const s = sec % 60;
      videoRecordingTime.value = `${String(min).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    }, 1000);

    videoAutoStopTimeout = window.setTimeout(() => stopVideoRecording(), 60000);

    videoMediaRecorder.start();
  } catch (err) {
    console.error("Error starting video recording:", err);
    $toast?.error?.("Unable to access camera.");
    isVideoRecording.value = false;
    if (videoStream) {
      videoStream.getTracks().forEach((t) => t.stop());
      videoStream = null;
    }
  }
};

const stopVideoRecording = () => {
  if (!isVideoRecording.value) return;
  isVideoRecording.value = false;
  if (videoRecordingTimer !== null) {
    clearInterval(videoRecordingTimer);
    videoRecordingTimer = null;
  }
  if (videoAutoStopTimeout !== null) {
    clearTimeout(videoAutoStopTimeout);
    videoAutoStopTimeout = null;
  }
  if (videoMediaRecorder && videoMediaRecorder.state !== "inactive") {
    videoMediaRecorder.stop();
  }
  if (videoStream) {
    videoStream.getTracks().forEach((t) => t.stop());
    videoStream = null;
  }
};

const resetVideo = () => {
  if (isVideoRecording.value) stopVideoRecording();
  videoMessage.value = null;
  videoRecordingTime.value = "00:00";
};

const handleSend = () => {
  if (!canSend.value || !props.conversation) return;

  const htmlContent = getEditorHTML(richTextEditor.value as any, messageContent.value);
  const hasAttachments = attachments.value.length > 0;
  const hasVoice = !!voiceMessage.value;

  const cleanedContent = isBodyEmpty(htmlContent) ? "" : htmlContent;

  const sendData: {
    text?: string;
    quote_id?: string;
    attachments?: File[];
    voice_message?: File;
    video_message?: File;
  } = {};

  if (cleanedContent || (!hasAttachments && !hasVoice)) {
    sendData.text = cleanedContent;
  }

  if (props.replyTo?.id) {
    sendData.quote_id = props.replyTo.id;
  }

  if (attachments.value.length) {
    sendData.attachments = [...attachments.value];
  }

  if (voiceMessage.value) {
    sendData.voice_message = voiceMessage.value;
  }

  if (videoMessage.value) {
    sendData.video_message = videoMessage.value;
  }

  emit("send-message", sendData);
  clearDraft(props.conversation.id);
  messageContent.value = "";
  attachments.value = [];
  resetVoice();
  resetVideo();
  showEmojiPopover.value = false;
};

const buildScheduledPayload = (scheduledAt: string) => {
  const htmlContent = getEditorHTML(richTextEditor.value as any, messageContent.value);

  const data: {
    text: string;
    quote_id?: string;
    attachments?: File[];
    voice_message?: File;
    video_message?: File;
    scheduledAt: string;
  } = {
    text: htmlContent,
    scheduledAt,
  };

  if (props.replyTo?.id) {
    data.quote_id = props.replyTo.id;
  }

  if (attachments.value.length) {
    data.attachments = attachments.value;
  }

  if (voiceMessage.value) {
    data.voice_message = voiceMessage.value;
  }

  if (videoMessage.value) {
    data.video_message = videoMessage.value;
  }

  return data;
};

const toggleScheduleMenu = () => {
  showScheduleMenu.value = !showScheduleMenu.value;
  if (showScheduleMenu.value) {
    closeAllPopovers();
  }
};

const handleScheduleMenuClick = () => {
  showScheduleMenu.value = false;
  showScheduleModal.value = true;
};

const showAiActionConfirm = ref(false);
const pendingAiActionMode = ref<"reply" | "followup" | null>(null);
const pendingAiActionIcpId = ref<string | undefined>(undefined);

const aiActionConfirmTitle = computed(() =>
  pendingAiActionMode.value === "followup" ? "Generate AI Follow-up" : "Generate AI Reply"
);
const aiActionConfirmMessage = computed(() =>
  pendingAiActionMode.value === "followup"
    ? "This will generate an AI-written follow-up message. Continue?"
    : "This will generate an AI-written reply. Continue?"
);

const confirmAiAction = () => {
  if (pendingAiActionMode.value === "reply") {
    emit("ai-reply", { icpId: pendingAiActionIcpId.value });
  } else if (pendingAiActionMode.value === "followup") {
    emit("ai-followup", { icpId: pendingAiActionIcpId.value });
  }
  showAiActionConfirm.value = false;
  pendingAiActionMode.value = null;
};

const cancelAiAction = () => {
  showAiActionConfirm.value = false;
  pendingAiActionMode.value = null;
};

const { showIcpPicker, icpPickerMode, triggerAiAction, selectIcp, dismissIcpPicker } = useIcpPicker(
  (mode, icpId) => {
    if (mode === "reply" || mode === "followup") {
      pendingAiActionMode.value = mode;
      pendingAiActionIcpId.value = icpId;
      showAiActionConfirm.value = true;
    } else {
      pendingComposeIcpId.value = icpId;
      showAiPanel.value = false;
      showAiComposePanel.value = true;
    }
  }
);

const toggleAiRefinePanel = () => {
  showAiComposePanel.value = false;
  showAiPanel.value = !showAiPanel.value;
};

const handleAiReply = () => {
  if (props.aiButtonsDisabled || props.isGeneratingAI) return;
  triggerAiAction("reply", props.icps);
};

const handleAiFollowup = () => {
  if (props.aiButtonsDisabled || props.isGeneratingAI) return;
  triggerAiAction("followup", props.icps);
};

const handleAiCompose = () => {
  if (props.aiButtonsDisabled || props.isGeneratingAI) return;
  triggerAiAction("compose", props.icps);
};

const handleAiComposeGenerate = (payload: { customInstructions: string }) => {
  emit("ai-compose", {
    icpId: pendingComposeIcpId.value,
    customInstructions: payload.customInstructions,
  });
  showAiComposePanel.value = false;
};

const handleAiCustomizationGenerate = async (payload: {
  customInstructions: string;
  attachments?: File[];
}) => {
  if (isGeneratingAiCustom.value) return;
  isGeneratingAiCustom.value = true;
  try {
    const response = await AIGenerationService.generateRefine({
      contactId: props.contactId || "",
      icpId: props.icpId,
      channel: props.channelName || props.conversation?.channel || "",
      currentDraft: messageContent.value,
      customInstructions: payload.customInstructions,
      files: payload.attachments,
    });
    setContent(response.body);
    showAiPanel.value = false;
    $toast?.success("AI message refined");
  } catch (error) {
    console.error("Failed to refine AI message:", error);
    $toast?.error("Failed to refine message");
  } finally {
    isGeneratingAiCustom.value = false;
  }
};

const handleScheduleSend = async (scheduleData: { scheduledAt: string }) => {
  if (isScheduling.value) {
    return;
  }

  try {
    isScheduling.value = true;
    const payload = buildScheduledPayload(scheduleData.scheduledAt);
    emit("schedule-send", payload);

    setTimeout(() => {
      showScheduleModal.value = false;
      attachments.value = [];
      resetVoice();
      resetVideo();
      isScheduling.value = false;
    }, 500);
  } catch (error) {
    console.error("Error scheduling send:", error);
    isScheduling.value = false;
  }
};

watch(
  () => props.replyTo,
  () => {
    if (!props.replyTo) {
      messageContent.value = "";
    }
  }
);

watch(showScheduleModal, (newVal) => {
  if (!newVal) {
    isScheduling.value = false;
  }
});

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement;

  if (
    showEmojiPopover.value &&
    !target.closest(".social-message-composer__emoji-container") &&
    !target.closest("[title='Insert emoji']")
  ) {
    closeEmojiPopover();
  }

  if (showLinkPopover.value && linkPopoverRef.value && !linkPopoverRef.value.contains(target)) {
    closeLinkPopover();
  }

  if (showScheduleMenu.value && sendButtonRef.value && !sendButtonRef.value.contains(target)) {
    showScheduleMenu.value = false;
  }
};

if (typeof window !== "undefined") {
  document.addEventListener("click", handleClickOutside);
}

watch(voiceMessageUrl, (newUrl, oldUrl) => {
  if (oldUrl) {
    URL.revokeObjectURL(oldUrl);
  }
});

watch(videoMessageUrl, (newUrl, oldUrl) => {
  if (oldUrl) {
    URL.revokeObjectURL(oldUrl);
  }
});

onBeforeUnmount(() => {
  if (voiceMessageUrl.value) {
    URL.revokeObjectURL(voiceMessageUrl.value);
  }
  if (videoMessageUrl.value) {
    URL.revokeObjectURL(videoMessageUrl.value);
  }
  resetVideo();
  if (props.conversation?.id) {
    const htmlContent = getEditorHTML(richTextEditor.value as any, messageContent.value);
    flushSave(props.conversation.id, isBodyEmpty(htmlContent) ? "" : htmlContent);
  }
  if (typeof window !== "undefined") {
    document.removeEventListener("click", handleClickOutside);
    window.removeEventListener("mousemove", handleEditorResizeMove);
    window.removeEventListener("mouseup", stopEditorResize);
  }
});

const setContent = (content: string) => {
  messageContent.value = content;
  nextTick(() => {
    if (richTextEditor.value) {
      const quill = getQuillInstance(richTextEditor.value as any);
      if (quill) {
        quill.root.innerHTML = content;
      }
    }
  });
};

let currentDraftConversationId = "";

const loadDraftForConversation = async (conversationId: string) => {
  currentDraftConversationId = conversationId;
  setContent("");
  if (!conversationId) return;
  const draftContent = await fetchDraft(conversationId);
  if (currentDraftConversationId === conversationId && draftContent) {
    setContent(draftContent);
  }
};

onMounted(() => {
  loadDraftForConversation(props.conversation?.id || "");
});

watch(
  () => props.conversation?.id,
  (newId, oldId) => {
    if (oldId) {
      const htmlContent = getEditorHTML(richTextEditor.value as any, messageContent.value);
      flushSave(oldId, isBodyEmpty(htmlContent) ? "" : htmlContent);
    }
    loadDraftForConversation(newId || "");
  }
);

watch(messageContent, (newVal) => {
  const conversationId = props.conversation?.id;
  if (!conversationId) return;
  const htmlContent = getEditorHTML(richTextEditor.value as any, newVal);
  scheduleSave(conversationId, isBodyEmpty(htmlContent) ? "" : htmlContent);
});

defineExpose({
  setContent,
  messageContent,
  richTextEditor,
});
</script>

<style scoped>
.social-message-composer {
  display: flex;
  flex-direction: column;
  background: #fff;
  border-top: 1px solid #e5e7eb;
  position: relative;
}

.social-message-composer__reply-tag {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;
}

.social-message-composer__reply-tag-text {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.social-message-composer__reply-tag-title {
  font-size: 0.9rem;
  font-weight: 700;
  color: #111827;
  line-height: 1.2;
}

.social-message-composer__reply-tag-snippet {
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.social-message-composer__reply-tag-close {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: #6b7280;
  border-radius: 999px;
  cursor: pointer;
}

.social-message-composer__reply-tag-close:hover {
  background: #f3f4f6;
  color: #111827;
}

.social-message-composer__attachments {
  display: flex;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.social-message-composer__attachment-btn {
  padding: 0.5rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 1.25rem;
  cursor: pointer;
  transition: all 0.2s;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
}

.social-message-composer__attachment-btn:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
  color: #374151;
}

.social-message-composer__resizable-area {
  position: relative;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

.social-message-composer__editor-wrapper {
  flex: 1;
  min-height: 0;
  border-bottom: 1px solid #e5e7eb;
  overflow: hidden;
}

.social-message-composer__resize-handle {
  position: absolute;
  top: -3px;
  left: 0;
  right: 0;
  height: 7px;
  cursor: ns-resize;
  z-index: 5;
  touch-action: none;
}

.social-message-composer__resize-handle::before {
  content: "";
  position: absolute;
  top: 2px;
  left: 50%;
  transform: translateX(-50%);
  width: 36px;
  height: 3px;
  border-radius: 999px;
  background: #d1d5db;
  transition: background 0.15s ease;
}

.social-message-composer__resize-handle:hover::before,
.social-message-composer__resize-handle.is-resizing::before {
  background: #3e3efe;
}

.social-message-composer__ai-buttons-wrapper {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 0.2rem 1rem 0.1rem;
  flex-shrink: 0;
}

.social-message-composer__footer {
  padding: 0.2rem 1rem;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
}

.social-message-composer__send-btn {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.4375rem 0.875rem;
  background: #3e3efe;
  color: white;
  border: none;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.social-message-composer__send-btn:hover:not(:disabled) {
  background: #3230d6;
}

.social-message-composer__send-btn:disabled,
.social-message-composer__send-btn--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.social-message-composer__footer-left {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.social-message-composer__footer-icon-btn {
  padding: 0.4rem;
  border-radius: 999px;
  border: none;
  background: #f3f4f6;
  color: #6b7280;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  transition: all 0.2s;
}

.social-message-composer__ai-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  height: 24px;
  font-size: 0.7rem;
  min-width: auto;
  white-space: nowrap;
  border-radius: 9999px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}

.social-message-composer__ai-btn-text {
  font-size: 0.7rem;
  font-weight: 500;
  line-height: 1;
}

.social-message-composer__ai-btn--reply {
  color: #7c3aed;
  background: rgba(124, 58, 237, 0.1);
}

.social-message-composer__ai-btn--reply:hover:not(:disabled) {
  background: rgba(124, 58, 237, 0.2);
  color: #6d28d9;
}

.social-message-composer__ai-btn--followup {
  color: #f59e0b;
  background: rgba(245, 158, 11, 0.1);
}

.social-message-composer__ai-btn--followup:hover:not(:disabled) {
  background: rgba(245, 158, 11, 0.2);
  color: #d97706;
}

.social-message-composer__ai-btn--compose {
  color: #0891b2;
  background: rgba(8, 145, 178, 0.1);
}

.social-message-composer__ai-btn--compose:hover:not(:disabled) {
  background: rgba(8, 145, 178, 0.2);
  color: #0e7490;
}

.social-message-composer__ai-btn--custom {
  color: #4b5563;
  background: rgba(107, 114, 128, 0.12);
}

.social-message-composer__ai-btn--custom:hover:not(:disabled) {
  background: rgba(107, 114, 128, 0.2);
  color: #374151;
}

.social-message-composer__ai-customization-overlay {
  position: absolute;
  inset: 0;
  z-index: 10001;
}

.social-message-composer__ai-customization-container {
  position: absolute;
  bottom: 56px;
  left: 0;
  z-index: 10002;
  pointer-events: auto;
}

.social-message-composer__ai-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.social-message-composer__ai-panel-title {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #1a1a2e;
}

.social-message-composer__ai-panel-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s;
}

.social-message-composer__ai-panel-close:hover {
  background: rgba(107, 114, 128, 0.12);
  color: #374151;
}

.social-message-composer__ai-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.social-message-composer__footer-icon-btn .iconify[data-icon*="loading"] {
  animation: spin 1s linear infinite;
}

.spin-icon {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.social-message-composer__footer-icon-btn:hover:not(:disabled) {
  background: #e5e7eb;
  color: #374151;
}

.social-message-composer__footer-icon-btn.is-recording {
  background: #fee2e2;
  color: #b91c1c;
}

.social-message-composer__recording-time {
  font-size: 0.75rem;
  color: #b91c1c;
}

.social-message-composer__footer-right {
  display: flex;
  align-items: center;
  position: relative;
}

.social-message-composer__footer-inline-tools {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin-right: 0.4rem;
}

.social-message-composer__split-button {
  display: inline-flex;
  align-items: stretch;
  border-radius: 999px;
  overflow: hidden;
}

.social-message-composer__send-dropdown-btn {
  width: 30px;
  border: none;
  background: #3e3efe;
  color: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.social-message-composer__split-button .social-message-composer__send-btn {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.social-message-composer__split-button .social-message-composer__send-dropdown-btn {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  border-left: 1px solid rgba(255, 255, 255, 0.2);
}

.social-message-composer__send-dropdown-btn:hover:not(:disabled) {
  background: #3230d6;
}

.social-message-composer__send-dropdown-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.social-message-composer__schedule-menu {
  position: absolute;
  bottom: calc(100% + 6px);
  right: 0;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  min-width: 160px;
  z-index: 20;
  padding: 4px;
}

.social-message-composer__schedule-menu-item {
  width: 100%;
  border: none;
  background: transparent;
  padding: 6px 10px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #111827;
  cursor: pointer;
  border-radius: 6px;
}

.social-message-composer__schedule-menu-item:hover {
  background: #f3f4f6;
}

.social-message-composer__audio-preview {
  height: 28px;
}

.social-message-composer__emoji-container {
  position: absolute;
  bottom: 56px;
  left: 16px;
  z-index: 30;
}

.social-message-composer__popover-wrapper {
  position: relative;
  z-index: 5;
  overflow: visible;
}

.social-message-composer__popover {
  position: fixed;
  z-index: 10000;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-width: calc(100vw - 32px);
  min-width: 240px;
}

.social-message-composer__popover--link {
  width: 240px;
  max-width: 240px;
}

.social-message-composer__popover-content {
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.social-message-composer__link-field-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
  position: relative;
}

.social-message-composer__link-field-icon {
  font-size: 16px;
  color: #6b7280;
  flex-shrink: 0;
}

.social-message-composer__link-input {
  flex: 1;
  padding: 5px 8px;
  border: 1px solid #d1d5db;
  border-radius: 2px !important;
  font-size: 12px;
  outline: none;
  line-height: 1.3;
  transition: border-color 0.2s;
  min-width: 0;
  box-sizing: border-box;
  min-height: 0;
  max-height: 28px;
}

.social-message-composer__link-input--text:focus {
  border-color: #3e3efe;
}

.social-message-composer__link-input--url {
  padding-right: 50px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.social-message-composer__link-input--url:focus {
  border-color: #3e3efe;
}

.social-message-composer__link-apply-btn {
  position: absolute;
  right: 2px;
  top: 2px;
  bottom: 2px;
  padding: 0 8px;
  border: none;
  border-radius: 2px !important;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  background: #3e3efe;
  color: #ffffff;
  display: flex;
  align-items: center;
  transition: background 0.2s;
  min-height: 0;
  max-height: 24px;
}

.social-message-composer__link-apply-btn:hover:not(:disabled) {
  background: #2e2ed8;
}

.social-message-composer__link-apply-btn:disabled {
  background: #e5e7eb;
  color: #9ca3af;
  cursor: not-allowed;
}

:deep(.rich-text-editor),
:deep(.rich-text-editor-wrapper) {
  height: 100%;
  border: none;
}

:deep(.ql-toolbar) {
  display: none;
}

:deep(.ql-container) {
  height: 100%;
  border: none;
  font-size: 0.875rem;
}

:deep(.ql-editor) {
  height: 100%;
  box-sizing: border-box;
  font-size: 0.875rem;
  padding: 0.5rem 1rem;
  overflow-y: auto;
  color: #111827;
  line-height: 1.5;
}

:deep(.ql-editor.ql-blank::before) {
  color: #9ca3af;
  font-style: normal;
}

.social-message-composer__previews {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.social-message-composer__preview-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0.75rem;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  transition: all 0.2s;
}

.social-message-composer__preview-item:hover {
  border-color: #d1d5db;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.social-message-composer__preview-icon {
  font-size: 1.5rem;
  color: #6b7280;
  flex-shrink: 0;
}

.social-message-composer__preview-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.social-message-composer__preview-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.social-message-composer__preview-size {
  font-size: 0.75rem;
  color: #6b7280;
}

.social-message-composer__preview-audio {
  height: 32px;
  flex: 1;
  max-width: 200px;
}

.social-message-composer__preview-video {
  flex: 1;
  max-width: 200px;
  max-height: 120px;
  border-radius: 8px;
}

.social-message-composer__video-live-preview {
  position: relative;
  align-self: flex-start;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  background: #000;
}

.social-message-composer__video-live {
  display: block;
  width: 160px;
  height: 120px;
  object-fit: cover;
}

.social-message-composer__video-live-badge {
  position: absolute;
  bottom: 6px;
  left: 6px;
  display: flex;
  align-items: center;
  gap: 5px;
  background: rgba(0, 0, 0, 0.65);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 7px;
  border-radius: 5px;
  letter-spacing: 0.02em;
}

.social-message-composer__video-live-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #ef4444;
  flex-shrink: 0;
  animation: video-live-blink 1s ease-in-out infinite;
}

@keyframes video-live-blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.2;
  }
}

.social-message-composer__preview-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  color: #6b7280;
  border-radius: 999px;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s;
}

.social-message-composer__preview-remove:hover {
  background: #f3f4f6;
  color: #111827;
}

/* ICP Picker positioning */
.social-message-composer__icp-picker-positioned {
  position: absolute;
  bottom: 100%;
  left: 16px;
  right: 16px;
  margin-bottom: 6px;
  z-index: 10;
}

.social-message-composer__ai-spinner {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid #d1d5db;
  border-top-color: #f97316;
  animation: ai-spin 0.7s linear infinite;
  display: inline-block;
  flex-shrink: 0;
}

@keyframes ai-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
